import React from 'react';
import RoundedInput from '../Inputs/RoundedInput';
import Switch from '../Switch';
import classes from './styles.module.scss';

const emptyForm = {
  name: '',
  billingAddress: '',
  city: '',
  state: '',
  zip: '',
};

const emptyFeatureFlags = {
  training_paths: true,
};

const toDateInputValue = (isoValue) => {
  if (!isoValue) return '';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

const toHourValue = (isoValue) => {
  if (!isoValue) return 0;
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return 0;
  return date.getHours();
};

const hourOptionLabel = (hour) => {
  const normalized = Number(hour);
  const isPm = normalized >= 12;
  const hour12 = normalized % 12 === 0 ? 12 : normalized % 12;
  return `${hour12} ${isPm ? 'PM' : 'AM'}`;
};

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

export default function OrganizationForm({
  formValues,
  formErrors,
  featureFlags,
  storageQuota,
  seatQuota,
  trialEndsAt,
  onFormChange,
  onFeatureFlagChange,
  onStorageQuotaChange,
  onSeatQuotaChange,
  onTrialEndsAtChange,
}) {
  const trialDateValue = toDateInputValue(trialEndsAt);
  const trialHourValue = toHourValue(trialEndsAt);

  return (
    <>
      <section className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <h3>Organization Details</h3>
        </div>

        <div className={classes.formGrid}>
          <RoundedInput
            label={formErrors.name || 'Organization name'}
            value={formValues.name}
            errors={formErrors.name}
            isTouched={!!formErrors.name}
            onChange={(event) => onFormChange('name', event.target.value)}
          />
          <RoundedInput
            label={formErrors.billingAddress || 'Billing address'}
            value={formValues.billingAddress}
            errors={formErrors.billingAddress}
            isTouched={!!formErrors.billingAddress}
            onChange={(event) =>
              onFormChange('billingAddress', event.target.value)
            }
          />
          <RoundedInput
            label={formErrors.city || 'City'}
            value={formValues.city}
            errors={formErrors.city}
            isTouched={!!formErrors.city}
            onChange={(event) => onFormChange('city', event.target.value)}
          />
          <RoundedInput
            label={formErrors.state || 'State'}
            value={formValues.state}
            errors={formErrors.state}
            isTouched={!!formErrors.state}
            onChange={(event) => onFormChange('state', event.target.value)}
          />
          <RoundedInput
            label={formErrors.zip || 'ZIP code'}
            value={formValues.zip}
            errors={formErrors.zip}
            isTouched={!!formErrors.zip}
            onChange={(event) => onFormChange('zip', event.target.value)}
          />
        </div>
      </section>

      <section className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <h3>Storage Quota</h3>
        </div>

        <div className={classes.storageQuotaContainer}>
          <div className={classes.storageQuotaInfo}>
            <h4>Storage Quota (MB)</h4>
            <p>Set storage limit for this organization. Leave empty for unlimited.</p>
          </div>
          <RoundedInput
            value={storageQuota}
            onChange={(event) => onStorageQuotaChange(event.target.value)}
            placeholder="Unlimited"
            type="number"
            min="0"
            style={{ maxWidth: '200px' }}
          />
        </div>
      </section>

      <section className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <h3>Seat Limit</h3>
        </div>

        <div className={classes.storageQuotaContainer}>
          <div className={classes.storageQuotaInfo}>
            <h4>Seat Limit (users)</h4>
            <p>Set the maximum number of users for this organization. Leave empty to use tier limit.</p>
          </div>
          <RoundedInput
            value={seatQuota}
            onChange={(event) => onSeatQuotaChange(event.target.value)}
            placeholder="Use tier limit"
            type="number"
            min="1"
            style={{ maxWidth: '200px' }}
          />
        </div>
      </section>

      <section className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <h3>Trial</h3>
        </div>

        <div className={classes.storageQuotaContainer}>
          <div className={classes.storageQuotaInfo}>
            <h4>Trial ends at</h4>
            <p>Set a trial end date for this organization. Leave empty to disable trial tracking.</p>
          </div>
          <div className={classes.trialDateRow}>
            <RoundedInput
              value={trialDateValue}
              onChange={(event) => {
                const nextDate = event.target.value;
                if (!nextDate) {
                  onTrialEndsAtChange?.('');
                  return;
                }
                const nextHour = trialHourValue ?? 0;
                const hh = String(nextHour).padStart(2, '0');
                onTrialEndsAtChange?.(`${nextDate}T${hh}:00`);
              }}
              placeholder="—"
              type="date"
              style={{ maxWidth: '320px' }}
            />
            <select
              className={classes.trialHourSelect}
              value={trialHourValue ?? 0}
              disabled={!trialDateValue}
              onChange={(event) => {
                const nextHour = Number(event.target.value);
                if (!trialDateValue) return;
                const hh = String(nextHour).padStart(2, '0');
                onTrialEndsAtChange?.(`${trialDateValue}T${hh}:00`);
              }}
            >
              {HOURS.map((hour) => (
                <option key={hour} value={hour}>
                  {hourOptionLabel(hour)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <h3>Feature flags</h3>
        </div>

        <div className={classes.featureFlagsGrid}>
          <div className={classes.featureFlagItem}>
            <div className={classes.featureFlagInfo}>
              <h4>Training Paths</h4>
              <p>Enable training paths functionality for this organization</p>
            </div>
            <Switch
              isChecked={featureFlags.training_paths}
              onClick={(event) => onFeatureFlagChange('training_paths', event.target.checked)}
            />
          </div>
        </div>
      </section>
    </>
  );
}

export { emptyForm, emptyFeatureFlags };
